// Son du jeu, en Web Audio : effets synthétisés (aucun fichier à charger), voix des dialogues, et les canaux où
// passent musiques et ambiances (voir soundtrack.ts). Chaque canal a son volume, sous le volume général.
import type { Voice } from '../../shared/voices.ts';
import { speakLetter } from '../common/voice-synth.ts';
import { playFootstep } from '../common/footstep-synth.ts';
import type { Footstep } from '../../shared/footsteps.ts';

export type SoundName = 'encounter' | 'select' | 'hit' | 'super' | 'faint' | 'capture' | 'victory' | 'levelup' | 'evolve' | 'evolved' | 'coin' | 'heal' | 'duel' | 'defeat'
  | 'dodge' | 'immune' | 'rest' | 'heartbeat' | 'tick' | 'slash' | 'whoosh' | 'boom' | 'thunder' | 'pet';

/** Une note ; `noise` : un souffle (bruit blanc) filtré, dont les aigus s'éteignent depuis `freq`, au lieu d'un son pur. */
type Note = { freq: number; at: number; duration: number; type?: OscillatorType; volume?: number; noise?: boolean };

const seq = (type: OscillatorType, freqs: number[], step: number, duration = step, volume = 0.12): Note[] =>
  freqs.map((freq, i) => ({ freq, at: i * step, duration, type, volume }));

/** Durée du trille d'évolution, calée sur la transformation (evolution-scene.ts). */
export const EVOLVE_CHARGE_S = 3.4;

/** Évolution en cours : un trille qui monte de deux octaves en s'accélérant, de plus en plus fort. */
function evolveCharge(): Note[] {
  const notes: Note[] = [];
  for (let i = 0, at = 0; at < EVOLVE_CHARGE_S; i++) {
    const progress = at / EVOLVE_CHARGE_S;
    const base = 262 * 2 ** (progress * 2);
    notes.push({ freq: i % 2 ? base * 1.5 : base, at, duration: 0.14, type: 'triangle', volume: 0.05 + progress * 0.07 });
    at += 0.3 - progress * 0.24;
  }
  return notes;
}

const SOUNDS: Record<SoundName, Note[]> = {
  encounter: seq('square', [392, 523, 659, 784, 659, 784], 0.07, 0.07, 0.08),
  select: seq('square', [880], 0.04, 0.04, 0.05),
  hit: seq('sawtooth', [220, 140], 0.05, 0.06, 0.1),
  super: seq('sawtooth', [330, 220, 110], 0.05, 0.07, 0.12),
  faint: seq('triangle', [440, 330, 247, 165], 0.09, 0.12, 0.12),
  capture: seq('square', [523, 659, 784, 1047], 0.09, 0.1, 0.08),
  victory: seq('square', [523, 523, 523, 698, 880, 784, 880], 0.1, 0.1, 0.08),
  levelup: seq('square', [523, 659, 784, 1047, 1319], 0.06, 0.08, 0.07),
  evolve: evolveCharge(),
  /** Évolution réussie : l'éclat de lumière, une montée, puis l'accord tenu. */
  evolved: [
    { freq: 7000, at: 0, duration: 0.8, volume: 0.28, noise: true },
    { freq: 98, at: 0, duration: 0.5, type: 'sine', volume: 0.3 },
    ...seq('square', [523, 659, 784, 1047], 0.08, 0.1, 0.06).map((note) => ({ ...note, at: note.at + 0.2 })),
    ...[784, 988, 1175, 1568].map((freq): Note => ({ freq, at: 0.55, duration: 1.4, type: 'triangle', volume: 0.07 })),
    ...seq('sine', [2093, 2637, 3136], 0.07, 0.25, 0.03).map((note) => ({ ...note, at: note.at + 0.6 })),
  ],
  coin: seq('square', [988, 1319], 0.06, 0.12, 0.06),
  heal: seq('sine', [523, 784, 1047, 784, 1047], 0.1, 0.12, 0.1),
  duel: seq('sawtooth', [196, 196, 262, 196, 311, 294], 0.1, 0.1, 0.07),
  defeat: seq('triangle', [392, 349, 311, 262], 0.15, 0.2, 0.1),
  /** Souffle de l'esquive : notes qui filent vers le grave. */
  dodge: seq('sine', [1200, 900, 650, 450], 0.025, 0.04, 0.06),
  /** Coup sans effet : étouffé, sourd. */
  immune: seq('triangle', [120, 95], 0.06, 0.1, 0.12),
  rest: seq('sine', [392, 494, 587, 784], 0.12, 0.18, 0.07),
  /** PV critiques : deux battements graves. */
  heartbeat: [{ freq: 62, at: 0, duration: 0.09, type: 'sine', volume: 0.35 }, { freq: 55, at: 0.16, duration: 0.11, type: 'sine', volume: 0.28 }],
  /** Couper : trois coups de lame rapides, puis le froissement du feuillage. */
  slash: [...seq('sawtooth', [900, 500], 0.06, 0.05, 0.07), ...seq('sawtooth', [900, 500], 0.06, 0.05, 0.07).map((n) => ({ ...n, at: n.at + 0.22 })),
    ...seq('triangle', [300, 220, 160], 0.07, 0.1, 0.08).map((n) => ({ ...n, at: n.at + 0.45 }))],
  /** Caresse du suiveur : petites notes douces qui montent. */
  pet: seq('sine', [784, 1047, 1319], 0.07, 0.12, 0.07),
  /** Voler : souffle qui s'élève. */
  whoosh: seq('sine', [260, 330, 400, 490, 590, 700, 830, 980, 1150], 0.09, 0.14, 0.07),
  /** Explosion (marqueur {boom} des dialogues) : un souffle grave qui s'éteint, sur un grondement. */
  boom: [
    { freq: 1400, at: 0, duration: 0.9, volume: 0.6, noise: true },
    { freq: 70, at: 0, duration: 0.55, type: 'sine', volume: 0.45 },
    { freq: 48, at: 0.08, duration: 0.7, type: 'sine', volume: 0.35 },
  ],
  /** Tonnerre (marqueur {éclair}) : un claquement sec, puis un long grondement qui roule et s'éteint. */
  thunder: [
    { freq: 5000, at: 0, duration: 0.18, volume: 0.35, noise: true },
    { freq: 700, at: 0.12, duration: 2.2, volume: 0.55, noise: true },
    { freq: 55, at: 0.15, duration: 1.6, type: 'sine', volume: 0.3 },
  ],
  /** Remplissage de la barre d'expérience. */
  tick: seq('square', [1568], 0.02, 0.02, 0.025),
};

const MUTE_KEY = 'pocket_muted';
const VOLUME_KEY = 'pocket_volume';
const channelKey = (channel: AudioChannel) => `pocket_volume_${channel}`;

/** Canaux du son, chacun avec son volume dans les options. */
export const AUDIO_CHANNELS = { music: 'Musique', ambience: 'Ambiance', effects: 'Effets spéciaux', voice: 'Voix des dialogues', footsteps: 'Bruits de pas' } as const;
export type AudioChannel = keyof typeof AUDIO_CHANNELS;
const CHANNELS = Object.keys(AUDIO_CHANNELS) as AudioChannel[];

/**
 * Volumes (0 à 1) d'un joueur qui n'a encore rien réglé dans ce navigateur : le général, puis chaque canal (ceux du
 * propriétaire du jeu, plus doux que tout à fond).
 */
const DEFAULT_VOLUME = 0.2;
const DEFAULT_CHANNEL_VOLUMES: Readonly<Record<AudioChannel, number>> = { music: 0.15, ambience: 0.1, effects: 0.2, voice: 0.6, footsteps: 0.45 };

/** Durée des changements de volume (curseur, sourdine), pour ne pas faire claquer le son. */
const GAIN_RAMP = 0.08;

/** Réglage retenu dans ce navigateur (le stockage peut être indisponible : navigation privée…). */
function readSetting(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSetting(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Pas de stockage : le réglage vaut pour cette visite seulement.
  }
}

/** Volume (0 à 1) retenu sous `key`, ou `fallback` s'il n'y en a pas (ou s'il est illisible). */
function readVolume(key: string, fallback: number): number {
  const saved = readSetting(key);
  const volume = saved === null ? fallback : Number(saved);
  return Number.isFinite(volume) ? Math.min(1, Math.max(0, volume)) : fallback;
}

export class SoundBoard {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private readonly buses = new Map<AudioChannel, GainNode>();
  private muted: boolean;
  /** Volume général, de 0 à 1. */
  private level: number;
  private readonly levels: Record<AudioChannel, number>;
  /** Baisse passagère d'un canal (musique pendant un dialogue…), multipliée à son volume. */
  private readonly ducks: Record<AudioChannel, number> = { music: 1, ambience: 1, effects: 1, voice: 1, footsteps: 1 };
  private readonly unlockListeners = new Set<() => void>();

  constructor() {
    this.muted = readSetting(MUTE_KEY) === '1';
    this.level = readVolume(VOLUME_KEY, DEFAULT_VOLUME);
    this.levels = Object.fromEntries(CHANNELS.map((channel) => [channel, readVolume(channelKey(channel), DEFAULT_CHANNEL_VOLUMES[channel])])) as Record<AudioChannel, number>;
    // Le navigateur ne laisse le son démarrer qu'après un geste du joueur : au premier, tout ce qui attendait part.
    const unlock = () => {
      const ctx = this.audio();
      void ctx.resume().then(() => {
        if (ctx.state !== 'running') return;
        for (const type of UNLOCK_EVENTS) window.removeEventListener(type, unlock, true);
        for (const listener of this.unlockListeners) listener();
      });
    };
    for (const type of UNLOCK_EVENTS) window.addEventListener(type, unlock, true);
  }

  get isMuted(): boolean {
    return this.muted;
  }

  get volume(): number {
    return this.level;
  }

  /** Le son peut-il jouer (le joueur a déjà touché l'écran ou le clavier) ? */
  get unlocked(): boolean {
    return this.context?.state === 'running';
  }

  /** `listener` est appelé quand le son se débloque (premier geste du joueur). */
  onUnlock(listener: () => void): void {
    this.unlockListeners.add(listener);
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    writeSetting(MUTE_KEY, muted ? '1' : '0');
    this.applyGains();
  }

  setVolume(volume: number): void {
    this.level = Math.min(1, Math.max(0, volume));
    writeSetting(VOLUME_KEY, String(this.level));
    this.applyGains();
  }

  channelVolume(channel: AudioChannel): number {
    return this.levels[channel];
  }

  setChannelVolume(channel: AudioChannel, volume: number): void {
    this.levels[channel] = Math.min(1, Math.max(0, volume));
    writeSetting(channelKey(channel), String(this.levels[channel]));
    this.applyGains();
  }

  /** Baisse (ou rend, `factor` 1) le volume d'un canal pendant un moment, en fondu sur `seconds`. */
  duck(channel: AudioChannel, factor: number, seconds = 0.4): void {
    this.ducks[channel] = factor;
    this.applyGains(seconds);
  }

  /** Contexte audio (créé à la première demande ; il reste suspendu jusqu'au premier geste du joueur). */
  audio(): AudioContext {
    if (this.context) return this.context;
    const ctx = new AudioContext();
    this.context = ctx;
    this.master = ctx.createGain();
    this.master.connect(ctx.destination);
    for (const channel of CHANNELS) {
      const bus = ctx.createGain();
      bus.connect(this.master);
      this.buses.set(channel, bus);
    }
    this.applyGains(0);
    return ctx;
  }

  /** Entrée d'un canal : ce qui s'y branche suit son volume et le volume général. */
  bus(channel: AudioChannel): GainNode {
    this.audio();
    return this.buses.get(channel)!;
  }

  play(name: SoundName): void {
    if (!this.audible('effects')) return;
    const ctx = this.audio();
    if (ctx.state === 'suspended') void ctx.resume();
    const bus = this.bus('effects');
    const start = ctx.currentTime + 0.01;
    for (const note of SOUNDS[name]) {
      const gain = ctx.createGain();
      const t0 = start + note.at, t1 = t0 + note.duration;
      gain.gain.setValueAtTime(note.volume ?? 0.1, t0);
      gain.gain.exponentialRampToValueAtTime(0.0001, t1);
      gain.connect(bus);
      const source = note.noise ? this.noise(ctx, note, t0, t1, gain) : this.tone(ctx, note, gain);
      source.start(t0);
      source.stop(t1 + 0.02);
    }
  }

  /** Une syllabe de la voix d'un dialogue, pour la lettre `char` qui vient de s'écrire. */
  speak(voice: Voice, char: string): void {
    if (!this.audible('voice') || !this.unlocked) return;
    speakLetter(this.audio(), this.bus('voice'), voice, char);
  }

  /** Un pas du joueur sur ce sol. */
  footstep(kind: Footstep): void {
    if (!this.audible('footsteps') || !this.unlocked) return;
    playFootstep(this.audio(), this.bus('footsteps'), kind);
  }

  private audible(channel: AudioChannel): boolean {
    return !this.muted && this.level > 0 && this.levels[channel] > 0;
  }

  private applyGains(seconds = GAIN_RAMP): void {
    const ctx = this.context;
    if (!ctx || !this.master) return;
    const now = ctx.currentTime;
    const ramp = (param: AudioParam, value: number) => {
      param.cancelScheduledValues(now);
      param.setValueAtTime(param.value, now);
      param.linearRampToValueAtTime(value, now + Math.max(seconds, 0.001));
    };
    ramp(this.master.gain, this.muted ? 0 : this.level);
    for (const channel of CHANNELS) ramp(this.buses.get(channel)!.gain, this.levels[channel] * this.ducks[channel]);
  }

  private tone(ctx: AudioContext, note: Note, gain: GainNode): OscillatorNode {
    const oscillator = ctx.createOscillator();
    oscillator.type = note.type ?? 'square';
    oscillator.frequency.value = note.freq;
    oscillator.connect(gain);
    return oscillator;
  }

  /** Bruit blanc passé dans un filtre passe-bas qui se referme (de `freq` vers le grave), relié au gain de la note. */
  private noise(ctx: AudioContext, note: Note, t0: number, t1: number, gain: GainNode): AudioBufferSourceNode {
    const buffer = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * (note.duration + 0.05)), ctx.sampleRate);
    const samples = buffer.getChannelData(0);
    for (let i = 0; i < samples.length; i++) samples[i] = Math.random() * 2 - 1;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(note.freq, t0);
    filter.frequency.exponentialRampToValueAtTime(60, t1);
    source.connect(filter).connect(gain);
    return source;
  }
}

/** Gestes du joueur qui débloquent le son. */
const UNLOCK_EVENTS = ['pointerdown', 'keydown', 'touchend'] as const;
